<template>
  <div
    ref="containerRef"
    class="dropdown-container"
  >
    <Button
      v-if="props.label"
      type="button"
      :icon="props.icon"
      :text="props.label"
      :variant="props.variant"
      size="sm"
      @click="menuOpen = !menuOpen"
    />
    <IconButton
      v-else-if="props.icon"
      :icon="props.icon"
      type="button"
      :variant="props.variant"
      @click="menuOpen = !menuOpen"
    />
    <menu
      v-if="menuOpen"
      class="dropdown-menu"
    >
      <template
        v-for="item in props.menuItems"
        :key="item.label"
      >
        <Button
          v-if="isMenuItemButton(item)"
          :text="item.label"
          :variant="item.buttonVariant"
          :icon="item.icon"
          size="sm"
          @click="
            () => {
              item.onClick();
              menuOpen = false;
            }
          "
        />
        <NestedDropdownMenu
          v-else-if="item.menuItems && item.label"
          :label="item.label"
          :icon="item.icon"
          :menu-items="item.menuItems"
          @click="menuOpen = false"
        />
        <li
          class="menu-item"
          v-else
          @click="
            (e) => {
              item.onClick(e);
              menuOpen = false;
            }
          "
        >
          <span class="material-symbols-outlined">
            {{ item.icon }}
          </span>
          <span>
            {{ item.label }}
          </span>
        </li>
        <slot></slot>
      </template>
    </menu>
  </div>
</template>
<script lang="ts">
export interface MenuItem {
  icon?: string;
  label?: string;
  onClick?: () => void;
  menuItems?: MenuItem[];
}
export interface MenuItemButton {
  icon?: string;
  label: string;
  buttonVariant:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'secondary-on-background'
    | 'destructive';
  onClick: () => void;
}
</script>
<script lang="ts" setup>
import { onBeforeUnmount, ref, watch } from 'vue';
import Button from '@component-module/components/Button/Button.vue';
import IconButton from '@component-module/components/IconButton/IconButton.vue';
import NestedDropdownMenu from './NestedDropdownMenu.vue';

const props = withDefaults(
  defineProps<{
    label?: string;
    icon?: string;
    variant?: 'primary' | 'secondary' | 'secondary-on-background';
    menuItems: Array<MenuItem | MenuItemButton>;
  }>(),
  {
    variant: 'secondary-on-background'
  }
);

const menuOpen = ref<boolean>(false);
const containerRef = ref<HTMLElement | null>(null);

const closeOnOutsidePointer = (e: PointerEvent) => {
  if (!containerRef.value?.contains(e.target as Node)) {
    menuOpen.value = false;
  }
};

const closeOnEscape = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    menuOpen.value = false;
  }
};

const addListeners = () => {
  document.addEventListener('pointerdown', closeOnOutsidePointer);
  document.addEventListener('keydown', closeOnEscape);
};

const removeListeners = () => {
  document.removeEventListener('pointerdown', closeOnOutsidePointer);
  document.removeEventListener('keydown', closeOnEscape);
};

watch(menuOpen, (open) => (open ? addListeners() : removeListeners()));
onBeforeUnmount(removeListeners);

const isMenuItemButton = (item: object): item is MenuItemButton => {
  return 'buttonVariant' in item;
};
</script>

<style lang="scss" scoped>
.dropdown-container {
  position: relative;
}
.dropdown-menu {
  min-width: 250px;
  display: grid;
  gap: $spacing-md;
  position: absolute;
  top: 46px; // the button is 38px tall
  right: 0;
  z-index: 100;
  font-size: 1rem;
  padding: 1rem 1.5rem;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: $spacing-md;
  cursor: pointer;
  &:hover {
    font-weight: 500;
  }
}
</style>
